import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthAgentService, GrowthStrategyArtifact } from './growth-agent.service';
import {
  GenerateGrowthMarketingPlanResponse,
  GrowthBrandVoiceProfileListItem,
  GrowthJourneyService,
  GrowthSwotLinkedArtifacts
} from './growth-journey.service';
import {
  brandVoiceTonePresetI18nKey,
  isStrategyArtifactApproved,
  normalizeStrategyArtifactStatus,
  swotProviderI18nKey
} from './growth-status.util';

interface MarketingPlanSmart {
  specific?: string;
  measurable?: string;
  achievable?: string;
  relevant?: string;
  timeBound?: string;
}

interface MarketingPlanGoal {
  id?: string;
  title?: string;
  smart?: MarketingPlanSmart;
}

interface MarketingPlanKpi {
  id?: string;
  goalId?: string;
  name?: string;
  metricKey?: string;
  targetValue?: unknown;
  unit?: string;
  periodDays?: unknown;
}

interface MarketingPlanSegment {
  id?: string;
  name?: string;
  description?: string;
  partySegmentRef?: string | null;
}

interface MarketingPlanChannel {
  channelKey?: string;
  weight?: number;
  rationale?: string;
  organicFirst?: boolean;
}

interface MarketingPlanPillar {
  id?: string;
  title?: string;
  themes?: string[];
  targetSegments?: string[];
}

interface MarketingPlanTimelineWeek {
  week?: number;
  milestones?: string[];
}

interface MarketingPlanPayload {
  planningHorizonDays?: number;
  goals?: MarketingPlanGoal[];
  objectives?: MarketingPlanGoal[];
  kpis?: MarketingPlanKpi[];
  kpiTargets?: MarketingPlanKpi[];
  segments?: MarketingPlanSegment[];
  channelMix?: MarketingPlanChannel[];
  contentPillars?: MarketingPlanPillar[];
  timeline?: MarketingPlanTimelineWeek[];
}

interface MarketingPlanPreview {
  planningHorizonDays?: number;
  goals: MarketingPlanGoal[];
  kpis: MarketingPlanKpi[];
  segments: MarketingPlanSegment[];
  channelMix: MarketingPlanChannel[];
  contentPillars: MarketingPlanPillar[];
  timeline: MarketingPlanTimelineWeek[];
}

@Component({
  standalone: true,
  selector: 'ain-growth-discovery-marketing-plan-panel',
  providers: [MessageService],
  imports: [
    FormsModule,
    RouterLink,
    AinFormFieldComponent,
    InfoHintComponent,
    AinStateComponent,
    ButtonModule,
    SelectModule,
    TableModule,
    TagModule
  ],
  template: `
    <section id="marketing-plan" class="marketing-plan-panel">
      <div class="section-title">
        <h3>{{ t('growth.journey.marketingPlan.panel.title') }}</h3>
      </div>

      <ain-info-hint
        [titleKey]="'growth.journey.marketingPlan.help.title'"
        [descriptionKey]="'growth.journey.marketingPlan.help.description'"
        [bulletKeys]="[
          'growth.journey.marketingPlan.help.bullets.draft',
          'growth.journey.marketingPlan.help.bullets.approval'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (autoGenerateWhenEmpty && generating() && !marketingPlanArtifact()) {
        <ain-state type="loading" [title]="t('growth.workflow.surfaces.journey.marketingPlan.autoGenerating')" />
      } @else {
        @if (lastGenerateResult(); as result) {
          <div class="meta-row">
            <p-tag [value]="providerLabel(result.provider)" severity="info" />
            @if (result.usedFallback) {
              <p-tag [value]="t('growth.journey.marketingPlan.fallbackUsed')" severity="warn" />
            }
          </div>
        }

        @if (marketingPlanArtifact(); as artifact) {
          <div class="meta-row">
            <p-tag [value]="artifactStatusLabel(artifact.status)" [severity]="artifactStatusSeverity(artifact.status)" />
          </div>

          <div class="artifact-links">
            <a
              pButton
              severity="secondary"
              [routerLink]="['/growth/agents/strategy-artifacts', artifact.id]"
              [label]="t('growth.journey.marketingPlan.artifacts.review')"></a>
          </div>

          @if (planPreview(); as preview) {
            <div class="preview-block">
              @if (preview.planningHorizonDays) {
                <p class="preview-meta">
                  <strong>{{ t('growth.journey.marketingPlan.fields.planningHorizon') }}:</strong>
                  {{ preview.planningHorizonDays }} {{ t('growth.journey.marketingPlan.fields.days') }}
                </p>
              }

              @if (preview.goals.length > 0) {
                <h4>{{ t('growth.journey.marketingPlan.sections.objectives') }}</h4>
                <p-table [value]="preview.goals">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.journey.marketingPlan.fields.goalTitle') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.measurable') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.timeBound') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ displayValue(row.title) }}</td>
                      <td>{{ displayValue(row.smart?.measurable) }}</td>
                      <td>{{ displayValue(row.smart?.timeBound) }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              }

              @if (preview.kpis.length > 0) {
                <h4>{{ t('growth.journey.marketingPlan.sections.kpis') }}</h4>
                <p-table [value]="preview.kpis">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.journey.marketingPlan.fields.kpiName') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.metricKey') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.targetValue') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.periodDays') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ displayValue(row.name) }}</td>
                      <td>{{ displayValue(row.metricKey) }}</td>
                      <td>{{ formatKpiTarget(row) }}</td>
                      <td>{{ displayValue(row.periodDays) }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              }

              @if (preview.segments.length > 0) {
                <h4>{{ t('growth.journey.marketingPlan.sections.segments') }}</h4>
                <p-table [value]="preview.segments">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.journey.marketingPlan.fields.segmentName') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.description') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ displayValue(row.name) }}</td>
                      <td>{{ displayValue(row.description) }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              }

              @if (preview.channelMix.length > 0) {
                <h4>{{ t('growth.journey.marketingPlan.channelMix') }}</h4>
                <p-table [value]="preview.channelMix">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.journey.marketingPlan.fields.channelKey') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.weight') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.rationale') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>
                        {{ displayValue(row.channelKey) }}
                        @if (row.organicFirst) {
                          <p-tag [value]="t('growth.journey.marketingPlan.fields.organicFirst')" severity="success" />
                        }
                      </td>
                      <td>{{ formatWeight(row.weight) }}</td>
                      <td>{{ displayValue(row.rationale) }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              }

              @if (preview.contentPillars.length > 0) {
                <h4>{{ t('growth.journey.marketingPlan.contentPillars') }}</h4>
                <p-table [value]="preview.contentPillars">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.journey.marketingPlan.fields.pillarTitle') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.themes') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ displayValue(row.title) }}</td>
                      <td>{{ (row.themes ?? []).join(', ') || '—' }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              }

              @if (preview.timeline.length > 0) {
                <h4>{{ t('growth.journey.marketingPlan.sections.timeline') }}</h4>
                <p-table [value]="preview.timeline">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.journey.marketingPlan.fields.week') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.milestones') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ displayValue(row.week) }}</td>
                      <td>{{ (row.milestones ?? []).join('; ') || '—' }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              }
            </div>
          }
        }

        @if (canGenerate()) {
          <ain-form-field [label]="t('growth.journey.positioning.fields.brandVoiceProfile')">
            <p-select
              [options]="brandVoiceOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedBrandVoiceProfileId"
              [disabled]="readOnly"
              [showClear]="true"
              appendTo="body"
              [placeholder]="t('growth.journey.positioning.fields.brandVoiceProfilePlaceholder')" />
          </ain-form-field>

          <div class="actions">
            <button
              pButton
              type="button"
              [label]="t('growth.journey.marketingPlan.generate')"
              [loading]="generating()"
              (click)="generate()"></button>
          </div>
        } @else if (!prerequisitesMet()) {
          <p class="hint-text">{{ prerequisitesMessage() }}</p>
        }

        @if (canCreateActivationPlan()) {
          <div class="actions">
            <button
              pButton
              type="button"
              severity="secondary"
              [label]="t('growth.journey.marketingPlan.activationPlan.fromPlan')"
              [loading]="creatingActivationPlan()"
              (click)="createActivationPlan()"></button>
            @if (lastActivationPlanId(); as planId) {
              <a
                pButton
                [routerLink]="['/growth/strategy/activation-plans', planId]"
                [label]="t('growth.journey.marketingPlan.activationPlan.view')"></a>
            }
          </div>
        }
      }

      @if (actionError()) {
        <p class="action-error">{{ actionError() }}</p>
      }
    </section>
  `,
  styles: [`
    .marketing-plan-panel { border-top: 1px solid #e5e7eb; padding-top: 12px; display: grid; gap: 12px; }
    .section-title h3, .preview-block h4 { margin: 0 0 8px; }
    .meta-row { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .artifact-links, .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .preview-block { display: grid; gap: 12px; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; background: #f9fafb; }
    .preview-meta { margin: 0; }
    .action-error { margin: 0; color: #b91c1c; }
    .hint-text { margin: 0; color: #6b7280; }
  `]
})
export class GrowthDiscoveryMarketingPlanPanelComponent implements OnInit, OnChanges {
  @Input({ required: true }) discoveryId!: string;
  @Input() latestPositioningArtifactId: string | null | undefined = null;
  @Input() latestMarketingPlanArtifactId: string | null | undefined = null;
  @Input() autoGenerateWhenEmpty = false;
  @Input() readOnly = false;
  @Output() discoveryChanged = new EventEmitter<void>();

  private autoGenerateAttempted = false;

  private readonly journey = inject(GrowthJourneyService);
  private readonly agentService = inject(GrowthAgentService);
  private readonly router = inject(Router);
  private readonly i18n = inject(LocalizationService);
  private readonly messages = inject(MessageService);

  readonly loading = signal(false);
  readonly generating = signal(false);
  readonly creatingActivationPlan = signal(false);
  readonly actionError = signal<string | null>(null);
  readonly artifacts = signal<GrowthSwotLinkedArtifacts | null>(null);
  readonly positioningArtifact = signal<GrowthStrategyArtifact | null>(null);
  readonly marketingPlanArtifact = signal<GrowthStrategyArtifact | null>(null);
  readonly planPreview = signal<MarketingPlanPreview | null>(null);
  readonly lastGenerateResult = signal<GenerateGrowthMarketingPlanResponse | null>(null);
  readonly lastActivationPlanId = signal<string | null>(null);
  readonly brandVoiceProfiles = signal<GrowthBrandVoiceProfileListItem[]>([]);

  selectedBrandVoiceProfileId: string | null = null;

  ngOnInit(): void {
    this.loadContext();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['latestPositioningArtifactId'] || changes['latestMarketingPlanArtifactId']) {
      this.loadLinkedArtifacts();
    }

    if (changes['discoveryId'] && !changes['discoveryId'].firstChange) {
      this.loadContext();
    }
  }

  swotApproved(): boolean {
    const swot = this.artifacts()?.swot;
    return !!swot && isStrategyArtifactApproved(swot.status);
  }

  positioningApproved(): boolean {
    const positioning = this.positioningArtifact();
    return !!positioning && isStrategyArtifactApproved(positioning.status);
  }

  prerequisitesMet(): boolean {
    return this.swotApproved() && this.positioningApproved();
  }

  canGenerate(): boolean {
    return !this.readOnly && this.prerequisitesMet() && !this.generating();
  }

  canCreateActivationPlan(): boolean {
    const artifact = this.marketingPlanArtifact();
    return !this.readOnly && !!artifact && isStrategyArtifactApproved(artifact.status) && !this.creatingActivationPlan();
  }

  prerequisitesMessage(): string {
    if (!this.swotApproved()) {
      return this.t('growth.journey.marketingPlan.errors.swotNotApproved');
    }

    return this.t('growth.journey.marketingPlan.errors.positioningNotApproved');
  }

  brandVoiceOptions(): Array<{ label: string; value: string }> {
    return this.brandVoiceProfiles()
      .filter(item => item.status !== 'Archived')
      .map(item => ({
        label: `${item.name} (${this.t(brandVoiceTonePresetI18nKey(item.tonePreset))})`,
        value: item.id
      }));
  }

  providerLabel(provider: string): string {
    return this.t(swotProviderI18nKey(provider));
  }

  artifactStatusLabel(status: unknown): string {
    const normalized = normalizeStrategyArtifactStatus(status);
    return this.t(`growth.journey.marketingPlan.status.${normalized.charAt(0).toLowerCase()}${normalized.slice(1)}`);
  }

  artifactStatusSeverity(status: unknown): 'secondary' | 'info' | 'success' | 'warn' | 'danger' | 'contrast' {
    const normalized = normalizeStrategyArtifactStatus(status);
    if (normalized === 'Approved') return 'success';
    if (normalized === 'InReview') return 'info';
    if (normalized === 'Archived') return 'secondary';
    return 'warn';
  }

  displayValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    return String(value);
  }

  formatKpiTarget(kpi: MarketingPlanKpi): string {
    const value = this.displayValue(kpi.targetValue);
    const unit = kpi.unit?.trim();
    return unit ? `${value} ${unit}` : value;
  }

  formatWeight(weight: number | undefined): string {
    if (weight === null || weight === undefined) {
      return '—';
    }

    return `${Math.round(weight * 100)}%`;
  }

  generate(): void {
    this.generateInternal(false);
  }

  createActivationPlan(): void {
    const artifact = this.marketingPlanArtifact();
    if (!artifact || !this.canCreateActivationPlan()) {
      return;
    }

    this.creatingActivationPlan.set(true);
    this.actionError.set(null);

    this.journey.createActivationPlanFromMarketingPlan(this.discoveryId, {
      idempotencyKey: `activation-from-plan-${this.discoveryId}-${Date.now()}`,
      marketingPlanArtifactId: artifact.id
    }).subscribe({
      next: result => {
        this.creatingActivationPlan.set(false);
        this.lastActivationPlanId.set(result.activationPlanId);
        this.messages.add({ severity: 'success', summary: this.t('growth.journey.marketingPlan.activationPlan.created') });
        this.router.navigate(['/growth/strategy/activation-plans', result.activationPlanId]);
      },
      error: err => {
        this.creatingActivationPlan.set(false);
        this.actionError.set(this.resolveActivationError(err));
      }
    });
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  private loadContext(): void {
    this.loading.set(true);
    this.actionError.set(null);

    this.journey.getSwotArtifacts(this.discoveryId).subscribe({
      next: linked => {
        this.artifacts.set(linked);
        this.loadLinkedArtifacts(true);
      },
      error: err => {
        this.loading.set(false);
        this.actionError.set(this.resolveActionError(err));
      }
    });

    this.journey.listBrandVoiceProfiles().subscribe({
      next: profiles => this.brandVoiceProfiles.set(profiles),
      error: () => this.brandVoiceProfiles.set([])
    });

    this.loadLinkedArtifacts();
  }

  private loadLinkedArtifacts(tryAutoGenerate = false): void {
    this.agentService.listStrategyArtifacts().subscribe({
      next: rows => {
        const positioningId = this.latestPositioningArtifactId;
        const marketingPlanId = this.latestMarketingPlanArtifactId;

        this.positioningArtifact.set(
          positioningId ? rows.find(item => item.id === positioningId) ?? null : null
        );

        const marketingPlan = marketingPlanId ? rows.find(item => item.id === marketingPlanId) ?? null : null;
        this.marketingPlanArtifact.set(marketingPlan);
        this.hydratePlanPreview(marketingPlan);
        this.loading.set(false);
        if (tryAutoGenerate) {
          this.tryAutoGenerate();
        }
      },
      error: () => {
        this.positioningArtifact.set(null);
        this.marketingPlanArtifact.set(null);
        this.planPreview.set(null);
        this.loading.set(false);
      }
    });
  }

  private tryAutoGenerate(): void {
    if (!this.autoGenerateWhenEmpty || this.autoGenerateAttempted || this.readOnly || this.marketingPlanArtifact()) {
      return;
    }

    this.autoGenerateAttempted = true;
    if (!this.canGenerate()) {
      return;
    }

    this.generateInternal(true);
  }

  private generateInternal(isAuto: boolean): void {
    if (!this.canGenerate()) {
      return;
    }

    this.generating.set(true);
    this.actionError.set(null);

    this.journey.generateMarketingPlan(this.discoveryId, {
      idempotencyKey: isAuto ? `marketing-plan-auto-${this.discoveryId}` : `marketing-plan-${this.discoveryId}-${Date.now()}`,
      brandVoiceProfileId: this.selectedBrandVoiceProfileId
    }).subscribe({
      next: result => {
        this.lastGenerateResult.set(result);
        this.generating.set(false);
        if (!isAuto) {
          this.messages.add({ severity: 'success', summary: this.t('growth.journey.marketingPlan.generated') });
        }
        this.discoveryChanged.emit();
        this.loadMarketingPlanArtifact(result.marketingPlanArtifactId);
      },
      error: err => {
        this.generating.set(false);
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  private loadMarketingPlanArtifact(artifactId: string): void {
    this.agentService.listStrategyArtifacts().subscribe({
      next: rows => {
        const marketingPlan = rows.find(item => item.id === artifactId) ?? null;
        this.marketingPlanArtifact.set(marketingPlan);
        this.hydratePlanPreview(marketingPlan);
      }
    });
  }

  private hydratePlanPreview(artifact: GrowthStrategyArtifact | null): void {
    if (!artifact?.artifactJson) {
      this.planPreview.set(null);
      return;
    }

    try {
      const payload = JSON.parse(artifact.artifactJson) as MarketingPlanPayload;
      this.planPreview.set({
        planningHorizonDays: payload.planningHorizonDays,
        goals: payload.goals ?? payload.objectives ?? [],
        kpis: payload.kpis ?? payload.kpiTargets ?? [],
        segments: payload.segments ?? [],
        channelMix: payload.channelMix ?? [],
        contentPillars: payload.contentPillars ?? [],
        timeline: payload.timeline ?? []
      });
    } catch {
      this.planPreview.set(null);
    }
  }

  private resolveActionError(error: unknown): string {
    const code = this.extractErrorCode(error);
    if (code === 'growth_marketing_plan_swot_not_approved' || code === 'growth_marketing_plan_swot_required') {
      return this.t('growth.journey.marketingPlan.errors.swotNotApproved');
    }

    if (code === 'growth_marketing_plan_positioning_not_approved' || code === 'growth_marketing_plan_positioning_required') {
      return this.t('growth.journey.marketingPlan.errors.positioningNotApproved');
    }

    if (code === 'growth_discovery_not_found') {
      return this.t('common.notFound');
    }

    return code ?? this.t('common.error');
  }

  private resolveActivationError(error: unknown): string {
    const code = this.extractErrorCode(error);
    if (code === 'growth_marketing_plan_artifact_not_approved') {
      return this.t('growth.journey.marketingPlan.errors.notApproved');
    }

    if (code === 'growth_marketing_plan_artifact_required') {
      return this.t('growth.journey.marketingPlan.errors.noPlan');
    }

    return this.resolveActionError(error);
  }

  private extractErrorCode(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const body = error.error as Record<string, unknown> | null;
    if (!body) {
      return null;
    }

    if (typeof body['error'] === 'string') {
      return body['error'];
    }

    if (typeof body['Error'] === 'string') {
      return body['Error'];
    }

    return null;
  }
}
